import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, filter, map } from 'rxjs/operators';
import { PartnerDataService } from '../system-configuration/services/partner-data.service';
import { Partners, partnerTypeEnum } from '../system-configuration/interfaces/partners';
import { PartnersConfigurationRequestDTO } from '../system-configuration/DTOs/request/partners-configuration-request-dto';
import { SystemConfigurationService } from '../system-configuration/services/system-configuration.service';
import { ResponseDTO } from '../system-configuration/DTOs/response/response-dto';
import { MessageService } from 'primeng/api';
import { ProgressIndicatingService } from '../../shared/services/progress-indicating.service';
import { ProgressData } from '../../shared/dtos/progress-data';
import { ResponseModel } from '../../shared/models/reponse.model';

@Component({
    selector: 'app-partner',
    templateUrl: './partner.component.html',
    styleUrls: ['./partner.component.scss']
})
export class PartnerComponent implements OnInit {
    public newPartnerType: { name: string, code: string }[] = [];
    public countryList: { name: string, code: string }[] = [];
    public displayHeader: string = 'Add New Partner';
    private ngUnsubscribe = new Subject();
    public loading: boolean = false;
    public errorCode: boolean = false;
    private editPartner: Partners = {};
    private progress$ = new Subject<ProgressData>();
    public hasLowerCasePassed : boolean = false;
    public hasUpperCasePassed : boolean = false;
    public hasNumberPassed : boolean = false;
    public hasSpecialCharactersPassed : boolean = false;
    public stringLengthPassed : boolean = false;
    public showMessage : string = '';
    public isDisablePswd: boolean = false;

    public constructor(public fb: FormBuilder,
                       private _partnerDataService: PartnerDataService,
                       private _router: Router,
                       private _systemConfigService: SystemConfigurationService,
                       private _messageService: MessageService,
                       private _progressIndicatingService: ProgressIndicatingService) { }

    public newPartnerForm = this.fb.group({
        partnerId: new FormControl('', [
            Validators.required,
            Validators.pattern(/^[a-zA-Z0-9_@$#!]+$/)]),
        partnerName: new FormControl('', [
            Validators.required,
            Validators.pattern(/^[a-zA-Z0-9 ]+$/)]),
        postalAddress: new FormControl('', [
            Validators.required
        ]),
        country: new FormControl(''),
        state: new FormControl(''),
        city: new FormControl(''),
        adminFirstName: new FormControl('', [
            Validators.required,
            Validators.pattern(/^[a-zA-Z ]+$/)
        ]),
        adminMiddleName: new FormControl(''),
        adminLastName: new FormControl('', [
            Validators.required,
            Validators.pattern(/^[a-zA-Z ]+$/)
        ]),
        postCode: new FormControl('', [
            Validators.required,
            Validators.pattern(/^[a-zA-Z0-9 ]+$/)
        ]),
        partnerType: new FormControl({}),
        email: new FormControl('', [
            Validators.required,
            Validators.pattern(
                /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            )
        ]),
        phoneNumber: new FormControl(null, [Validators.required, Validators.maxLength(12)]),
        VATno: new FormControl(''),
        isTopconPartner: new FormControl(false),
        partnerStatus: new FormControl(''),
        creditCheckId: new FormControl(''),
        bankName: new FormControl(''),
        branch: new FormControl(''),
        IBANno: new FormControl(''),
        sortCode: new FormControl(''),
        bankPostCode: new FormControl(''),
        password: new FormControl(''),
        clientId: new FormControl('', [Validators.pattern(/^[a-zA-Z0-9-]+$/)])
    });

    public ngOnInit(): void {
        this.newPartnerType = [
            { name: 'Organization', code: 'O' },
            { name: 'Organization Unit', code: 'OU' },
            { name: 'Authorization Domain', code: 'AD' }
        ];
        this.loadCustomers();
        this.newPartnerForm.get('password')?.valueChanges.subscribe((data: string) => {
            this.hasLowerCasePassed = this.hasLowerCase(data);
            this.hasUpperCasePassed = this.hasUpperCase(data);
            this.hasNumberPassed = this.hasNumber(data);
            this.hasSpecialCharactersPassed = this.hasSpecialCharacter(data);
            this.stringLengthPassed = this.stringLength(data);
        });
    }

    public hasLowerCase(str : string) : boolean {
        return (/[a-z]/.test(str));
    }

    public hasUpperCase(str : string) : boolean {
        return (/[A-Z]/.test(str));
    }

    public hasNumber(str : string) : boolean {
        return (/[0-9]/.test(str));
    }

    public hasSpecialCharacter(str : string) : boolean {
        return (/[ `!@#$%^&*()_+\-=\\[\]{};':"\\|,.<>\\/?~]/.test(str));
    }

    public stringLength(str : string) : boolean {
        return str?.length >= 10;
    }

    private loadCustomers() : void {
        this._progressIndicatingService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Loading Country Details',
                details: 'Please wait...',
                done: false
            }
        });
        this._partnerDataService.getConfigurationSelectionContract().pipe(
            filter(response => new ResponseModel(response).successful),
            map(res => res.selections)
        ).subscribe(data => {
            data?.countries.forEach(country => {
                this.countryList.push({ name: country.name!, code: country.countryabbr! });
            });
            this.sendProgressDoneSignal();
            this.getData();
        }, error => {
            this.apiResponsePopup(error.error.responseMessage, error.error.responseCode);
            this.sendProgressDoneSignal();
        });
    }

    private getPartnerName(value: string): string {
        if (value === 'O') {
            return partnerTypeEnum.O;
        } else if (value === 'OU') {
            return partnerTypeEnum.OU;
        } else if (value === 'AD') {
            return partnerTypeEnum.AD;
        } else { return ''; }
    }

    public getData(): void {
        this._partnerDataService.data.pipe(takeUntil(this.ngUnsubscribe)).subscribe(response => {
            this.editPartner = response;
            if (response !== null && Object.values(response).length > 0) {
                this.newPartnerForm.patchValue({
                    partnerId: response.partnerId,
                    email: response.email,
                    isTopconPartner: response.isTopconPartner,
                    partnerName: response.organizationDisplayName,
                    phoneNumber: isNaN(+response.phone!) ? 0 : response.phone,
                    state: response.state,
                    city: response.city,
                    country: this.countryList.find(x => x.code === response.country)?.name,
                    adminFirstName: response.adminFirstName,
                    adminLastName: response.adminLastName,
                    postCode: response.zipcode,
                    VATno: response.taxPayerId,
                    postalAddress: response.address1,
                    partnerType: { name: this.getPartnerName(response.organizationType!), code: response.organizationType! },
                    clientId: response.clientId
                });
                this.displayHeader = 'Edit Partner';
                this.newPartnerForm.controls.partnerId.disable();
            } else {
                this.displayHeader = 'Add New Partner';
            }
        });
    }

    public navigateToPartner() : void {
        this.newPartnerForm.reset();
        this.editPartner = {};
        this._partnerDataService.sendData(null);
        this._router.navigate(['/admin/system-configuration']);
    }

    public saveDetails(value: any) : void {
        const partnerId = this.newPartnerForm.getRawValue().partnerId;
        this.errorCode = false;
        let partner: Partners = {};
        if (this.displayHeader === 'Edit Partner') {
            partner = {
                organizationId: this.editPartner.organizationId,
                partnerId: partnerId,
                organizationDisplayName: value.partnerName,
                isTopconPartner: value.isTopconPartner,
                phone: value.phoneNumber,
                state: value.state,
                city: value.city,
                adminFirstName: value.adminFirstName,
                adminLastName: value.adminLastName,
                email: value.email,
                zipcode: value.postCode,
                address1: value.postalAddress,
                resendLimit: 0,
                organizationType: value.partnerType.code,
                country: this.countryList.find(x => x.name === value.country)?.code,
                ...(!value.isTopconPartner && { clientId: value.clientId })

            };
        } else {
            partner = {
                organizationId: value.organizationId ? value.organizationId : 0,
                partnerId: partnerId,
                organizationDisplayName: value.partnerName,
                isTopconPartner: value.isTopconPartner,
                phone: value.phoneNumber,
                address2: value.state,
                address3: value.city,
                adminFirstName: value.adminFirstName,
                adminLastName: value.adminLastName,
                email: value.email,
                zipcode: value.postCode,
                address1: value.postalAddress,
                resendLimit: 0,
                organizationType: value.partnerType?.code,
                country: this.countryList.find(x => x.name === value.country)?.code,
                password: value.password ? btoa(value.password) : '',
                ...(!value.isTopconPartner && { clientId: value.clientId })

            };
        }
        partner.password = partner.password ? btoa(partner.password!) : '';
        const partnersRequestObj: PartnersConfigurationRequestDTO = {
            configurationData: partner
        };
        this.loading = true;
        if (this.displayHeader === 'Edit Partner') {
            this._progressIndicatingService.show({
                progress$: this.progress$.asObservable(),
                initial: {
                    header: 'Updating Partner In Progress',
                    details: 'Please wait...',
                    done: false
                }
            });
            this._systemConfigService.updatePartner(partnersRequestObj)
                .subscribe((data: ResponseDTO) => {
                    this.loading = false;
                    this.apiResponsePopup(data.responseMessage!, data.responseCode!);
                    this.sendProgressDoneSignal();
                }, error => {
                    this.apiResponsePopup(error.error.responseMessage, error.error.responseCode);
                    this.sendProgressDoneSignal();
                });
        } else {
            this._progressIndicatingService.show({
                progress$: this.progress$.asObservable(),
                initial: {
                    header: 'Saving Partner In Progress',
                    details: 'Please wait...',
                    done: false
                }
            });
            this._systemConfigService.addNewPartner(partnersRequestObj)
                .subscribe((data: ResponseDTO) => {
                    this.loading = false;
                    this.apiResponsePopup(data.responseMessage!, data.responseCode!);
                    this.sendProgressDoneSignal();
                }, error => {
                    this.apiResponsePopup(error.error.responseMessage, error.error.responseCode);
                    this.sendProgressDoneSignal();
                });
        }
    }

    private sendProgressDoneSignal(): void {
        this.progress$.next({
            header: '',
            done: true
        });
    }

    public cancelDetails(): void {
        if (this.displayHeader === 'Edit Partner') {
            this.newPartnerForm.patchValue({
                partnerId: this.editPartner.partnerId,
                email: this.editPartner.email,
                isTopconPartner: this.editPartner.isTopconPartner,
                partnerName: this.editPartner.organizationDisplayName,
                phoneNumber: isNaN(+this.editPartner.phone!) ? 0 : this.editPartner.phone,
                state: this.editPartner.state,
                city: this.editPartner.city,
                country: this.countryList.find(x => x.code === this.editPartner.country)?.name,
                adminFirstName: this.editPartner.adminFirstName,
                adminLastName: this.editPartner.adminLastName,
                postCode: this.editPartner.zipcode,
                VATno: this.editPartner.taxPayerId,
                postalAddress: this.editPartner.address1,
                partnerType: { name: this.getPartnerName(this.editPartner.organizationType!), code: this.editPartner.organizationType! },
                clientId: this.editPartner.clientId
            });
        } else {
            this.newPartnerForm.reset();
        }
    }

    public onChangeTopcon(event: {checked : boolean}) : void {
        if (event.checked === true && this.displayHeader !== 'Edit Partner') {
            this.newPartnerForm.get('password')?.setValidators([Validators.required, Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[ `!@#$%^&*()_+\-=\\[\]{};':"\\|,.<>\\/?~])(?=.{10,})/)]);
            this.newPartnerForm.get('password')?.updateValueAndValidity();
        } else {
            this.newPartnerForm.get('password')?.clearValidators();
            this.newPartnerForm.get('password')?.updateValueAndValidity();
            this.newPartnerForm.patchValue({
                password: ''
            });
        }
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    private apiResponsePopup(responseMsg: string, responseCode: number) : void {
        if (responseCode === 0) {
            this._messageService.add({ severity: 'success', summary: 'Success', detail: responseMsg });
            this._router.navigateByUrl('/admin/system-configuration');
            this.newPartnerForm.reset();
            this._partnerDataService.sendData(null);
        } else {
            // this.newPartnerForm.reset();
            this._messageService.add({ severity: 'error', summary: 'Fail', detail: responseMsg });
        }
    }
}
